/* eslint-disable react-hooks/exhaustive-deps */
import EditIcon from "@mui/icons-material/Edit";
import UploadIcon from "@mui/icons-material/Upload";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import FilePicker from "components/FilePicker";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import configData from "config/config.json";
import usePaginationHook from "hooks/usePagination";
import useSearchHook from "hooks/useSearch";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getLeagues } from "services/leagueServices";
import sessionHelper from "services/sessionHelper";
import { uploadLeagueWeigtage } from "services/uploadService";
import { defaultPageNo } from "utils/constants";
import { opacity2hex, sortArray } from "utils/miscUtils";
import { PermittedRoles } from "utils/permissions";
import { getLeagueTableColumns } from "utils/tableHelper/leaguesTableHelper";

const useStyles = makeStyles(((theme: any) => ({
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  pickerContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "end",
    justifyContent: "space-between",
  },
  buttonContainer: {
    minWidth: 90,
    maxWidth: 120,
    display: "flex",
  },
  button: {
    height: 30,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    width: "100% !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      maxWidth: "100% !important",
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
})) as any);

const RowActions = ({ rowId, data }: any) => {
  const navigate = useNavigate();

  const handleRowAction = (data: any) => {
    navigate(configData.routes.leagues.edit?.replace(":id", data?._id));
  };

  return (
    <CustomButton
      onClick={() => handleRowAction(data)}
      startIcon={<EditIcon />}
    >
      Edit
    </CustomButton>
  );
};

const Leagues = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [listData, setListData] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.LEAGUE.UPDATE_ROLES
  );
  const [fileDetails, setFileDetails] = React.useState({
    file: null,
    fileType: ".xlsx, .xls",
  });

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: listData?.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...listData], orderDirection, column);
    setListData(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const createLeague = () => navigate(configData.routes.leagues.create);

  const fetchData = async (_search?: any) => {
    setLoading(true);
    try {
      let payload = { pageNo, pageSize };
      if (searchValue) payload["searchValue"] = searchValue;
      const resp = await getLeagues(payload);

      setListData(resp?.data?.items);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  const onSearch = (value: any) => {
    // reset filters if any
    _onSearch(value);
    onChangePage(defaultPageNo);
  };

  const onSelectFile = (file: any) => {
    if (!file) {
      setFileDetails((st) => ({ ...st, file: null }));
      return;
    }
    setFileDetails((st) => ({ ...st, file }));
  };

  const handleUpload = async () => {
    try {
      setLoading(true);
      const response = await uploadLeagueWeigtage(fileDetails.file);
      setLoading(false);
      setFileDetails((st) => ({ ...st, file: null }));
      if (response.status === 200 || response.status === true)
        showSnackbar(response.data?.statusText);
      else throw new Error(response.statusText);
    } catch (error) {
      setLoading(false);
      setFileDetails((st) => ({ ...st, file: null }));
      showSnackbar(error.message ?? "Error uploading file", "error");
    }
  };

  useEffect(() => {
    const initialColumns = getLeagueTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission
    );
    setColumns(initialColumns);

    fetchData();
  }, [pageNo, pageSize, searchValue]);

  return (
    <ContentShell
      title="Leagues & Milestones"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a league"
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.LEAGUE.CREATE_ROLES) && (
            <CustomButton onClick={createLeague}>Create</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.container}>
        {hasEditPermission && (
          <Box className={styles.pickerContainer}>
            <FilePicker
              label="League Weightage"
              name="leagueWeightage"
              fileType={fileDetails.fileType}
              value={fileDetails.file}
              showPreview={false}
              onFileSelect={onSelectFile}
            />
            <Box className={styles.buttonContainer}>
              <CustomButton
                type="submit"
                disabled={!fileDetails.file}
                startIcon={<UploadIcon />}
                onClick={handleUpload}
              >
                Upload
              </CustomButton>
            </Box>
          </Box>
        )}
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={listData.length}
          highlightAltRow={true}
          actions={RowActions}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
        />
      </Box>
    </ContentShell>
  );
};

export default Leagues;
